import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreateAgreementData } from '@/domain/agreements/agreement';
import { UserStatus } from '@/domain/auth/auth';
import { OfferingStatus, ServiceType } from '@/domain/offerings/offering';
import { useClient, useClients } from '@/hooks/api/useClients';
import { useOfferings } from '@/hooks/api/useOfferings';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SelectSearch } from '@/components/features/shared/SelectSearch';

interface ClientServiceSelectorProps {
  form: ReturnType<typeof useForm<CreateAgreementData>>;
}

export const ClientServiceSelector = ({
  form,
}: ClientServiceSelectorProps) => {
  const [clientSearch, setClientSearch] = useState('');

  const selectedUserId = form.watch('userId');
  const { data: selectedClient } = useClient(selectedUserId);
  const selectedOfferingId = form.watch('offeringId');
  const serviceType = form.watch('serviceType');

  const clientFilters = useMemo(
    () => ({ search: clientSearch, status: UserStatus.Active, limit: 100 }),
    [clientSearch],
  );

  const { data: clientsResponse } = useClients(clientFilters);

  const { data: offeringsResponse } = useOfferings({
    status: OfferingStatus.ACTIVE,
    isAdditional: false,
  });

  const clients = clientsResponse?.data;
  const offerings = offeringsResponse;

  const filteredOfferings = useMemo(
    () =>
      offerings?.filter((offering) =>
        serviceType ? offering.serviceType === serviceType : true
      ),
    [offerings, serviceType]
  );

  useEffect(() => {
    if (selectedOfferingId && offerings) {
      const selectedOffering = offerings.find((o) => o.id === selectedOfferingId);
      if (selectedOffering && selectedOffering.basePrice) {
        const currentAmount = form.getValues('amount');
        if (
          currentAmount === 0 ||
          !currentAmount ||
          currentAmount !== selectedOffering.basePrice
        ) {
          form.setValue('amount', selectedOffering.basePrice);
        }
      }
    } else if (!selectedOfferingId && filteredOfferings && filteredOfferings.length === 1) {
      form.setValue('offeringId', filteredOfferings[0].id);
    }
  }, [selectedOfferingId, offerings, serviceType]);

  const handleServiceTypeChange = (value: ServiceType) => {
    form.setValue('serviceType', value);
    form.setValue('offeringId', '');
    form.setValue('sellCode', undefined);
  };

  const formatServiceType = (type: ServiceType): string => {
    const types: Record<ServiceType, string> = {
      [ServiceType.CREMACIONES]: 'Cremaciones',
      [ServiceType.ABONOS]: 'Abonos',
      [ServiceType.PARCELAS]: 'Parcelas',
    };
    return types[type] || type;
  };

  const clientItems = [
    ...(clients || []).filter((client) => client.id !== selectedClient?.id).map((client) => ({
      id: client.id,
      label: `${client.name} - N° ${client.code}`,
    })),
    ...(selectedClient
      ? [
          {
            id: selectedClient.id,
            label: `${selectedClient.userData.legalName} - N° ${selectedClient.userData.code}`,
          },
        ]
      : []),
  ];

  const offeringItems = (filteredOfferings || []).map((offering) => ({
    id: offering.id,
    label: (
      <div className="flex gap-3">
        <span>{offering.name}</span>
        <span className="text-muted-foreground">Código: {offering.code}</span>
        <span className="text-muted-foreground font-mono">${offering.basePrice}</span>
      </div>
    ),
  }));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Cliente</label>
        <SelectSearch
          value={form.watch('userId')}
          onValueChange={(value) => form.setValue('userId', value)}
          onSearchChange={setClientSearch}
          placeholder="Seleccionar cliente"
          searchPlaceholder="Buscar cliente..."
          emptyMessage="No se encontraron clientes."
          items={clientItems}
        />
        {form.formState.errors.userId && (
          <p className="text-sm text-red-500">{form.formState.errors.userId?.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Tipo de Servicio</label>
        <Select onValueChange={handleServiceTypeChange} value={serviceType}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar tipo de servicio" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(ServiceType)
              .filter((type) => type !== ServiceType.PARCELAS)
              .map((type) => (
                <SelectItem key={type} value={type}>
                  {formatServiceType(type)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Servicio</label>
        <SelectSearch
          value={form.watch('offeringId')}
          onValueChange={(value) => form.setValue('offeringId', value)}
          placeholder={
            serviceType ? 'Seleccionar servicio' : 'Primero seleccione un tipo de servicio'
          }
          searchPlaceholder="Buscar servicio..."
          disabled={!serviceType}
          emptyMessage="No se encontraron servicios."
          items={offeringItems}
        />
        {form.formState.errors.offeringId && (
          <p className="text-sm text-red-500">
            {form.formState.errors.offeringId?.message}
          </p>
        )}
      </div>
    </div>
  );
};
